import { CloseIconButton } from '../../../../shared/components/IconButton';
import Pill from '@ingka/pill';
import { ProgressBounds } from '../../../../shared/types/progress.types';
import React from 'react';
import styled from '@emotion/styled';
import { theme } from '../../../../shared/styles/core/theme';
import { tokens } from '../../../../shared/styles/core/tokens';
import { useFiltering } from '../../state/filtering.state';

type FilterValue =
   | string
   | String
   | number
   | Number
   | boolean
   | Boolean
   | ProgressBounds
   | string[]
   | String[]
   | ProgressBounds[]
   | Date[]
   | Date
   | undefined;

type FilterPillProps = {
   columnId: string;
   header?: string;
   value: FilterValue;
};

const StyledOptionContainer = styled.div`
   display: inline-flex;
   align-items: center;
   border-radius: 25px;
   padding: 0px 5px;
   margin: 0px 5px;
   background: ${theme.colors.bg.primary};
`;

const StyledPillSpan = styled.span`
   display: flex;
   align-items: center;
`;

const StyledLabelSpan = styled.span`
   color: ${theme.colors.text.primary};
   padding-right: ${tokens.space.sm};
`;

const StyledValueSpan = styled.span`
   font-weight: ${tokens.font.weight.normal};
`;

const StyledPill = styled(Pill)`
   background: ${theme.colors.bg.tertiary};
   cursor: default;
   border-color: ${theme.colors.bg.primary};

   :active {
      background: ${theme.colors.bg.tertiary};
   }

   :hover {
      height: 2.5rem;
      max-height: 2.5rem;
      text-decoration: auto;
      border-color: ${theme.colors.bg.primary};
   }
`;

export const FilterPill = ({ columnId, header, value }: FilterPillProps) => {
   const { removeFilter } = useFiltering();

   function toUserFriendlyString(input: FilterValue): string {
      if (input === undefined) {
         return '';
      }
      if (typeof input === 'boolean' || input instanceof Boolean) {
         return '';
      }
      if (typeof input === 'number' || input instanceof Number) {
         return input.toString();
      }
      if (input instanceof Date) {
         return input.toLocaleDateString();
      }
      if (Array.isArray(input)) {
         if (input.length === 0) return '';
         if (input[0] instanceof Date) {
            return input.map(date => (date as Date).toLocaleDateString()).join(', ');
         }
         if (typeof input[0] === 'object' && !(input[0] instanceof String)) {
            return input.map(bound => `${(bound as ProgressBounds).lower}-${(bound as ProgressBounds).upper}`).join(', ');
         }
         return input.map(item => item.toString()).join(', ');
      }
      if (typeof input === 'object' && 'lower' in input && 'upper' in input) {
         return `${input.lower}-${input.upper}`;
      }
      return input.toString();
   }

   const isArrayValue = Array.isArray(value);
   const valueArray = isArrayValue ? (value as (string | String | Date | ProgressBounds)[]) : [];

   return (
      <StyledPill
         label={!isArrayValue ? (
            <StyledPillSpan>
               <StyledLabelSpan>
                  {header}{toUserFriendlyString(value) !== '' ? ': ' : ''}
                  <StyledValueSpan>{toUserFriendlyString(value)}</StyledValueSpan>
               </StyledLabelSpan>
               <CloseIconButton
                  onClick={() => removeFilter(columnId)}
                  title="Remove filter"
               />
            </StyledPillSpan>
         ) : (
            <StyledPillSpan>
               <StyledLabelSpan>
                  {header}{valueArray.length > 0 ? ':' : ''}{' '}
                  {valueArray.length > 1 ? (
                     valueArray.map((val, index) => (
                        <StyledOptionContainer key={index}>
                           <StyledValueSpan>{toUserFriendlyString(val)}</StyledValueSpan>
                           <CloseIconButton
                              onClick={() => removeFilter(columnId, val)}
                              title="Remove filter"
                           />
                        </StyledOptionContainer>
                     ))
                  ) : (
                     <StyledValueSpan>
                        {valueArray[0] ? toUserFriendlyString(valueArray[0]) : ''}
                     </StyledValueSpan>
                  )}
               </StyledLabelSpan>
               <CloseIconButton
                  onClick={() => removeFilter(columnId)}
                  title="Remove filter"
               />
            </StyledPillSpan>
         )}
         iconPosition={'trailing'}
         size={'small'}
      />
   );
};
